import React from 'react';

export type ColumnAlign = 'left' | 'right' | 'center';
export type ColumnFormat = 'text' | 'currency' | 'percent' | 'custom';

export interface Column<T> {
  key: keyof T | string;
  header: string;
  align?: ColumnAlign;
  format?: ColumnFormat;
  muted?: boolean;
  width?: string;
  render?: (value: unknown, row: T) => React.ReactNode;
}

interface DataTableProps<T extends Record<string, unknown>> {
  columns: Column<T>[];
  data: T[];
  highlightRow?: (row: T, index: number) => boolean;
  caption?: string;
  compact?: boolean;
  bordered?: boolean;
}

export function DataTable<T extends Record<string, unknown>>({
  columns,
  data,
  highlightRow,
  caption,
  compact = false,
  bordered = false,
}: DataTableProps<T>) {
  const getAlignment = (align?: ColumnAlign): string => {
    switch (align) {
      case 'right': return 'text-right';
      case 'center': return 'text-center';
      default: return 'text-left';
    }
  };

  const formatValue = (value: unknown, format?: ColumnFormat): React.ReactNode => {
    if (value === null || value === undefined) return '—';

    switch (format) {
      case 'currency':
        return typeof value === 'number'
          ? `$${value.toLocaleString()}`
          : String(value);
      case 'percent':
        return typeof value === 'number'
          ? `${value}%`
          : String(value);
      default:
        return String(value);
    }
  };

  const getCellValue = (row: T, column: Column<T>): unknown => {
    const key = column.key as string;
    if (key.includes('.')) {
      return key.split('.').reduce((obj: Record<string, unknown>, k) => obj?.[k] as Record<string, unknown>, row as Record<string, unknown>);
    }
    return row[key as keyof T];
  };

  const padding = compact ? 'py-1.5 px-2' : 'py-2 px-3';
  const borderStyle = bordered ? 'border border-gray-300' : 'border-b border-gray-100';
  const headerBorderStyle = bordered ? 'border border-gray-300' : 'border-b border-gray-200';

  return (
    <div className="overflow-x-auto">
      <table className={`w-full text-sm ${bordered ? 'border-collapse border border-gray-300' : ''}`}>
        {caption && (
          <caption className="text-xs text-gray-500 text-left mb-2">{caption}</caption>
        )}
        <thead>
          <tr className={bordered ? 'bg-gray-100' : ''}>
            {columns.map((column, i) => (
              <th
                key={i}
                className={`${padding} font-semibold ${column.muted ? 'text-gray-500' : 'text-gray-700'} ${getAlignment(column.align)} ${headerBorderStyle}`}
                style={column.width ? { width: column.width } : undefined}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => {
            const isHighlighted = highlightRow?.(row, rowIndex);
            return (
              <tr
                key={rowIndex}
                className={isHighlighted ? 'bg-gray-100' : (rowIndex % 2 === 1 && !bordered ? 'bg-gray-50/50' : '')}
              >
                {columns.map((column, colIndex) => {
                  const value = getCellValue(row, column);
                  const displayValue = column.render
                    ? column.render(value, row)
                    : formatValue(value, column.format);

                  return (
                    <td
                      key={colIndex}
                      className={`${padding} ${column.muted ? 'text-gray-500' : 'text-gray-600'} ${getAlignment(column.align)} ${borderStyle} ${isHighlighted ? 'font-medium' : ''}`}
                    >
                      {displayValue}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
